import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Loading } from 'barte-design-system';
import { useQueryClient } from '@tanstack/react-query';

import {
  useConfigureTerminals,
  useUnconfigureTerminals,
} from '@/modules/backoffice/services/terminals';
import { BACKOFFICE_QUERIES } from '@/modules/backoffice/services/queries';
import { StepLinkConfig } from '../terminal-operation-drawer/steppers/step-link-config';
import { OperationResultTable } from '../operation-result-table';

import {
  getOptionsForStatus,
  needsConfig,
  groupBy,
  getErrorInfo,
  downloadCsv,
} from './batch-unlink-drawer.utils';
import type { OperationOption } from './batch-unlink-drawer.utils';
import styles from './batch-unlink-drawer.module.css';
import type {
  BatchUnlinkDrawerProps,
  BatchUnlinkStep,
  BatchResultItem,
  BatchOperationType,
  BatchActionMode,
  Seed,
} from './batch-unlink-drawer.type';
import type { IPOSCompanyItem } from '@/modules/backoffice/types/company';
import type { ITerminalListItem } from '@/modules/backoffice/types/terminal';
import type { ILinkAssignment } from '../terminal-operation-drawer/terminal-operation-drawer.type';
import type { CompanyGroup } from './batch-unlink-drawer.type';

const toFailedResult = (t: ITerminalListItem): BatchResultItem => ({
  serialNumber: t.serialNumber,
  posCompanyId: t.posCompanyId ?? null,
  intermediador: t.posCompanyName ?? null,
  posSellerId: t.posSellerId ?? null,
  posSellerName: t.posSellerName ?? null,
  success: false,
  errorStatus: null,
});

export const computeSeed = (
  initialOperationType: BatchOperationType | undefined,
  companyGroups: CompanyGroup[],
): Seed | null => {
  if (!initialOperationType) return null;
  if (initialOperationType === 'ASSOCIATE_SELLER') {
    const first = companyGroups[0];
    if (!first || first.companyId == null) return null;
    return {
      operationType: initialOperationType,
      step: 'config',
      payfac: {
        id: first.companyId,
        name: first.companyName,
        posCompanyId: first.posCompanyId,
      },
    };
  }
  if (needsConfig(initialOperationType)) {
    return { operationType: initialOperationType, step: 'config', payfac: null };
  }
  return { operationType: initialOperationType, step: null, payfac: null };
};

export const BatchUnlinkDrawer = ({
  isOpen,
  onClose,
  selectedTerminals,
  currentStatus,
  actionMode,
  initialOperationType,
  onSuccess,
}: BatchUnlinkDrawerProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<BatchUnlinkStep>('choose');
  const [operationType, setOperationType] = useState<BatchOperationType | null>(null);
  const [selectedPayfac, setSelectedPayfac] = useState<IPOSCompanyItem | null>(null);
  const [linkAssignments, setLinkAssignments] = useState<ILinkAssignment[]>([]);

  const [results, setResults] = useState<BatchResultItem[]>([]);

  const { mutateAsync: configureMutation } = useConfigureTerminals();
  const { mutateAsync: unconfigureMutation } = useUnconfigureTerminals();

  const seededRef = useRef(false);

  const handleClose = () => onClose();

  useEffect(() => {
    if (isOpen) return;
    setStep('choose');
    setOperationType(null);
    setSelectedPayfac(null);
    setLinkAssignments([]);
    setResults([]);
    seededRef.current = false;
  }, [isOpen]);

  const companyGroups = useMemo((): CompanyGroup[] => {
    const groups = groupBy(selectedTerminals.filter((t) => t.posCompanyId), (t) => t.posCompanyId);
    return Object.entries(groups).map(([companyId, terminals]) => ({
      posCompanyId: Number(companyId),
      companyId: terminals[0].companyId ?? null,
      companyName: terminals[0].posCompanyName ?? String(companyId),
      terminals,
    }));
  }, [selectedTerminals]);

  const allSerialNumbers = useMemo(
    () => selectedTerminals.map((t) => t.serialNumber),
    [selectedTerminals],
  );

  useEffect(() => {
    if (!isOpen || seededRef.current) return;
    const seed = computeSeed(initialOperationType, companyGroups);
    if (!seed) return;
    seededRef.current = true;
    setOperationType(seed.operationType);
    if (seed.payfac) setSelectedPayfac(seed.payfac);
    if (seed.step) setStep(seed.step);
  }, [isOpen, initialOperationType, companyGroups]);

  const isConfigReady = (): boolean => {
    if (operationType === 'LINK_PAYFAC_ONLY') return selectedPayfac !== null;
    if (operationType === 'LINK_BOTH') {
      return selectedPayfac !== null && !linkAssignments.some((a) => a.serialNumbers.length === 0);
    }
    if (operationType === 'ASSOCIATE_SELLER') {
      return selectedPayfac !== null && !linkAssignments.some((a) => a.serialNumbers.length === 0);
    }
    return false;
  };

  const buildResultsFromResponse = (
    response: { success: boolean; data?: { success: import('@/modules/backoffice/types/terminal').IConfigureTerminalResultItem[]; errors: import('@/modules/backoffice/types/terminal').IConfigureTerminalResultItem[] } },
    groupTerminals: typeof selectedTerminals,
  ): BatchResultItem[] => {
    if (!response.success) {
      return groupTerminals.map(toFailedResult);
    }

    if (
      response.data &&
      Array.isArray(response.data.success) &&
      Array.isArray(response.data.errors)
    ) {
      return [
        ...response.data.success.map((s) => ({
          serialNumber: s.serialNumber,
          posCompanyId: s.actualPayfacId ?? null,
          intermediador: s.actualPayfacName ?? null,
          posSellerId: s.actualSellerId ?? null,
          posSellerName: s.actualSellerName ?? null,
          success: true,
          errorStatus: null,
        })),
        ...response.data.errors.map((e) => ({
          serialNumber: e.serialNumber,
          posCompanyId: e.actualPayfacId ?? null,
          intermediador: e.actualPayfacName ?? null,
          posSellerId: e.actualSellerId ?? null,
          posSellerName: e.actualSellerName ?? null,
          success: false,
          errorStatus: e.status ?? null,
        })),
      ];
    }

    return groupTerminals.map((t) => ({
      serialNumber: t.serialNumber,
      posCompanyId: t.posCompanyId ?? null,
      intermediador: t.posCompanyName ?? null,
      posSellerId: t.posSellerId ?? null,
      posSellerName: t.posSellerName ?? null,
      success: true,
      errorStatus: null,
    }));
  };

  const buildErrorResults = (): BatchResultItem[] =>
    selectedTerminals.map(toFailedResult);

  const processBatch = async () => {
    setStep('processing');
    const allResults: BatchResultItem[] = [];

    try {
      if (operationType === 'DISASSOCIATE_SELLER') {
        const ungroupedTerminals = selectedTerminals.filter((t) => !t.posCompanyId);
        allResults.push(...ungroupedTerminals.map(toFailedResult));

        const settled = await Promise.allSettled(
          companyGroups.map((group) => {
            if (group.companyId == null) return Promise.reject(new Error('MISSING_COMPANY_ID'));
            return configureMutation({
              payfacId: group.companyId,
              assignments: [{ serialNumbers: group.terminals.map((tx) => tx.serialNumber) }],
            });
          }),
        );
        settled.forEach((outcome, i) => {
          const group = companyGroups[i];
          if (outcome.status === 'fulfilled') {
            allResults.push(...buildResultsFromResponse(outcome.value, group.terminals));
          } else {
            allResults.push(...group.terminals.map(toFailedResult));
          }
        });

      } else if (operationType === 'UNLINK_PAYFAC') {
        const response = await unconfigureMutation({ serialNumbers: allSerialNumbers });
        allResults.push(...buildResultsFromResponse(response, selectedTerminals));

      } else if (operationType === 'ASSOCIATE_SELLER') {
        const assignments: { sellerId?: number; serialNumbers: string[] }[] = linkAssignments
          .filter((a) => a.serialNumbers.length > 0)
          .map((a) => ({ sellerId: a.sellerId, serialNumbers: a.serialNumbers }));
        const unassigned = allSerialNumbers.filter(
          (s) => !linkAssignments.some((a) => a.serialNumbers.includes(s)),
        );
        if (unassigned.length > 0) assignments.push({ serialNumbers: unassigned });
        const response = await configureMutation({
          payfacId: selectedPayfac!.id,
          assignments,
        });
        allResults.push(...buildResultsFromResponse(response, selectedTerminals));

      } else if (operationType === 'LINK_PAYFAC_ONLY') {
        const response = await configureMutation({
          payfacId: selectedPayfac!.id,
          assignments: [{ serialNumbers: allSerialNumbers }],
        });
        allResults.push(...buildResultsFromResponse(response, selectedTerminals));

      } else if (operationType === 'LINK_BOTH') {
        const assignments: { sellerId?: number; serialNumbers: string[] }[] = linkAssignments
          .filter((a) => a.serialNumbers.length > 0)
          .map((a) => ({ sellerId: a.sellerId, serialNumbers: a.serialNumbers }));
        const unassigned = allSerialNumbers.filter(
          (s) => !linkAssignments.some((a) => a.serialNumbers.includes(s)),
        );
        if (unassigned.length > 0) assignments.push({ serialNumbers: unassigned });
        const response = await configureMutation({
          payfacId: selectedPayfac!.id,
          assignments,
        });
        allResults.push(...buildResultsFromResponse(response, selectedTerminals));
      }

      await queryClient.invalidateQueries({ queryKey: [BACKOFFICE_QUERIES.TERMINALS] });
      onSuccess();
      setResults(allResults);
      setStep('results');
    } catch {
      setResults(buildErrorResults());
      setStep('results');
    }
  };

  const handleChooseConfirm = () => {
    if (needsConfig(operationType)) {
      if (operationType === 'ASSOCIATE_SELLER' && companyGroups[0]?.companyId != null) {
        setSelectedPayfac({
          id: companyGroups[0].companyId,
          name: companyGroups[0].companyName,
          posCompanyId: companyGroups[0].posCompanyId,
        });
      }
      setStep('config');
    } else {
      processBatch();
    }
  };

  const successCount = results.filter((r) => r.success).length;
  const errorCount = results.filter((r) => !r.success).length;

  const operationLabel = (() => {
    if (operationType === 'DISASSOCIATE_SELLER') return t('backoffice.terminalsBatchResultDisassociateSeller');
    if (operationType === 'UNLINK_PAYFAC') return t('backoffice.terminalsBatchResultUnlinkPayfac');
    if (operationType === 'ASSOCIATE_SELLER') return t('backoffice.terminalsBatchResultAssociateSeller');
    if (operationType === 'LINK_PAYFAC_ONLY') return t('backoffice.terminalsBatchResultLinkPayfac');
    if (operationType === 'LINK_BOTH') return t('backoffice.terminalsBatchResultLinkBoth');
    return '';
  })();

  const allOptions = getOptionsForStatus(currentStatus);
  const optionFilters: Record<BatchActionMode, (opts: OperationOption[]) => OperationOption[]> = {
    unlink: (opts) => opts.filter((o) => o.type === 'UNLINK_PAYFAC' || o.type === 'DISASSOCIATE_SELLER'),
    associate: (opts) => opts.filter((o) => o.type === 'ASSOCIATE_SELLER'),
    link: (opts) => opts,
  };
  const options = optionFilters[actionMode ?? 'link'](allOptions);

  const getFooter = () => {
    if (step === 'choose') {
      return {
        primaryButton: {
          children: t(needsConfig(operationType)
            ? 'backoffice.terminalsBatchAdvance'
            : 'backoffice.terminalsBatchUnlinkConfirm'),
          onClick: handleChooseConfirm,
          disabled: operationType === null,
        },
      };
    }

    if (step === 'config') {
      return {
        secondaryButton: {
          children: t('backoffice.terminalsBatchBack'),
          variant: 'outline' as const,
          onClick: () => setStep('choose'),
        },
        primaryButton: {
          children: t('backoffice.terminalsBatchUnlinkConfirm'),
          onClick: processBatch,
          disabled: !isConfigReady(),
        },
      };
    }

    if (step === 'results') {
      return {
        secondaryButton: errorCount > 0
          ? {
              children: t('backoffice.terminalsBatchUnlinkDownloadErrors'),
              variant: 'outline' as const,
              onClick: () => downloadCsv(results, operationLabel, t),
            }
          : undefined,
        primaryButton: {
          children: t('backoffice.terminalsBatchUnlinkClose'),
          onClick: handleClose,
        },
      };
    }

    return undefined;
  };

  const isLinkOperation = operationType === 'LINK_PAYFAC_ONLY' || operationType === 'LINK_BOTH' || operationType === 'ASSOCIATE_SELLER';

  const drawerOnCloseByStep: Partial<Record<BatchUnlinkStep, () => void>> = {
    processing: () => {},
  };

  const showSellerSection = operationType !== 'LINK_PAYFAC_ONLY';

  const tooltipTitles = {
    success: t('backoffice.terminalsDrawerTooltipSuccessTitle'),
    error: t('backoffice.terminalsDrawerTooltipErrorTitle'),
  };

  const getTooltipBody = (item: BatchResultItem): string => {
    if (!item.success) return item.errorStatus ? t(getErrorInfo(item.errorStatus).messageKey) : '';
    const successBodies = {
      link: t('backoffice.terminalsDrawerTooltipSuccessLinkBody'),
      unlink: t('backoffice.terminalsDrawerTooltipSuccessUnlinkBody'),
    };
    return isLinkOperation ? successBodies.link : successBodies.unlink;
  };

  const resultTableItems = results.map((item) => ({
    serialNumber: item.serialNumber,
    entityId: item.posCompanyId,
    entityName: item.intermediador,
    subEntityId: item.posSellerId,
    subEntityName: item.posSellerName,
    success: item.success,
    tooltipTitle: item.success ? tooltipTitles.success : tooltipTitles.error,
    tooltipBody: getTooltipBody(item),
  }));

  return (
    <Drawer
      isOpen={isOpen}
      title={t('backoffice.terminalsDrawerTitle')}
      onClose={drawerOnCloseByStep[step] ?? handleClose}
      footer={getFooter()}
      className={styles.drawer}
    >
      {step === 'choose' && (
        <div className={styles.content}>
          <div className={styles.header}>
            <span className={styles.title}>
              {t('backoffice.terminalsBatchChooseTitle')}
            </span>
            <span className={styles.subtitle}>
              {t('backoffice.terminalsBatchChooseSubtitle', { count: selectedTerminals.length })}
            </span>
          </div>

          <div className={styles.options}>
            {options.map((option) => (
              <button
                key={option.type}
                type="button"
                className={[
                  styles['option-card'],
                  operationType === option.type ? styles['option-card--selected'] : '',
                ].join(' ')}
                onClick={() => setOperationType(option.type)}
              >
                <div className={styles['option-icon']}><option.Icon /></div>
                <div className={styles['option-body']}>
                  <span className={styles['option-label']}>{t(option.labelKey)}</span>
                  <span className={styles['option-description']}>{t(option.descriptionKey)}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'config' && (operationType === 'ASSOCIATE_SELLER' || operationType === 'LINK_PAYFAC_ONLY' || operationType === 'LINK_BOTH') && (
        <StepLinkConfig
          allSerialNumbers={allSerialNumbers}
          selectedPayfac={selectedPayfac}
          linkAssignments={showSellerSection ? linkAssignments : []}
          onPayfacChange={(payfac) => {
            setSelectedPayfac(payfac);
            setLinkAssignments([]);
          }}
          onAssignmentsChange={showSellerSection ? setLinkAssignments : () => {}}
          isSubmitting={false}
          showSellerSection={showSellerSection}
        />
      )}

      {step === 'processing' && (
        <div className={styles.processing}>
          <Loading type="icon" description={t('backoffice.terminalsBatchUnlinkProcessingTitle')} />
          <span className={styles['processing-subtitle']}>
            {t('backoffice.terminalsBatchUnlinkProcessingSubtitle')}
          </span>
        </div>
      )}

      {step === 'results' && (
        <div className={styles.results}>
          <span className={styles['results-subtitle']}>
            {t('backoffice.terminalsBatchUnlinkResultSubtitle')}
          </span>

          <div className={styles['results-section']}>
            <div className={styles['results-section-header']}>
              <span className={styles['results-operation-label']}>{operationLabel}</span>
              <div className={styles['results-badges']}>
                {successCount > 0 && (
                  <span className={styles['badge-success']}>
                    {t('backoffice.terminalsBatchUnlinkSuccessCount', { count: successCount })}
                  </span>
                )}
                {errorCount > 0 && (
                  <span className={styles['badge-error']}>
                    {t('backoffice.terminalsBatchUnlinkErrorCount', { count: errorCount })}
                  </span>
                )}
              </div>
            </div>

            <OperationResultTable
              items={resultTableItems}
              entityColumnLabel={t('backoffice.terminalsBatchUnlinkResultColumnIntermediary')}
              subEntityColumnLabel={t('backoffice.terminalsBatchUnlinkResultColumnSeller')}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
};
